import { captureException } from '@sentry/browser';
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import EPTierProtection from '@/comp/common/EPTierProtection';
import EPTierLock from '@/comp/common/EPTierLock';
import ErrorModal from '@/comp/ErrorModal';
import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { ComponentTierName, interestedPackageEnum } from '@/constants/enums';
import { useStores } from '@/hooks/use-stores';
import LockIcon from '@/comp/common/icons/LockIcon';
import CreateNewControllerTier from '../CreateNewControllerTier';
import ControlCenterCompressorWidget from '../ControlCenterCompressorWidget/ControlCenterCompressorWidget';
import UniversalDeviceWidget from '../UniversalDeviceWidget/UniversalDeviceWidget';
import { SpinCentered } from '@/comp';
import { EPSelect, EPSelectOption } from '@/components';
import { Container } from './ControlCenterAssets.styles';
import {
  filterAndSortDevicesForControlCenter,
  getDeviceFilterOptions,
  type DeviceFilterType,
} from './ControlCenterAssets.utils';

const ControlCenterAssets = observer(() => {
  const { stationStore, controlCenterStore, devicesStore } = useStores();
  const { t } = useTranslation();
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilterType>('all');
  const [isSchemaErrorModalOpen, setIsSchemaErrorModalOpen] = useState(false);
  const [lastSchemaError, setLastSchemaError] = useState<string | null>(null);

  const allDevices = stationStore.currentStation?.devices || [];

  const devicesList = React.useMemo(() => {
    return filterAndSortDevicesForControlCenter(allDevices, deviceFilter, devicesStore);
  }, [allDevices, deviceFilter, devicesStore]);

  const generalDevicesIdsKey = React.useMemo(() => {
    const ids = new Set<string>();
    for (const device of allDevices) {
      if (device.deviceType === DeviceType.General) ids.add(device.deviceId);
    }
    return Array.from(ids).sort().join('|');
  }, [allDevices]);

  useEffect(() => {
    const loadSchemas = async () => {
      const stationId = stationStore.currentStation?.id;
      if (!stationId) return;

      try {
        await controlCenterStore.fetchDeviceSchemas({
          stationId,
          generalDevicesIds: generalDevicesIdsKey ? generalDevicesIdsKey.split('|') : [],
        });
      } catch (error) {
        captureException(error);
      }
    };

    void loadSchemas();
  }, [controlCenterStore, stationStore.currentStation?.id, generalDevicesIdsKey]);

  useEffect(() => {
    if (controlCenterStore.schemasError && controlCenterStore.schemasError !== lastSchemaError) {
      setIsSchemaErrorModalOpen(true);
      setLastSchemaError(controlCenterStore.schemasError);
      return;
    }
    if (!controlCenterStore.schemasError) {
      setIsSchemaErrorModalOpen(false);
      setLastSchemaError(null);
    }
  }, [controlCenterStore.schemasError, lastSchemaError]);

  const shouldShowLoading = !stationStore.currentStation || controlCenterStore.isSchemasLoading;

  return (
    <Container>
      <ErrorModal
        condition={isSchemaErrorModalOpen}
        resetCallback={() => setIsSchemaErrorModalOpen(false)}
        errorText={controlCenterStore.schemasError || t('Something went wrong')}
      />
      {shouldShowLoading && <SpinCentered />}
      {!shouldShowLoading && (
        <div className="main-wrapper ">
          <div className="filter-and-lock-wrapper">
            <div className="filter-wrapper">
              <EPSelect
                value={deviceFilter}
                onChange={value => setDeviceFilter(value as DeviceFilterType)}
                style={{ width: 200 }}
              >
                {getDeviceFilterOptions(allDevices, devicesStore).map((opt) => (
                  <EPSelectOption key={opt.value} value={opt.value}>
                    {t(opt.label)}
                  </EPSelectOption>
                ))}
              </EPSelect>
            </div>
            <div className="tier-lock-wrapper">
              <EPTierProtection componentName={ComponentTierName.StationControlCenterView}>
                <div className="lock-wrapper">
                  <EPTierLock
                    label={t('enableSystemControl')}
                    icon={<LockIcon />}
                    tooltipTextPart2={t('alwaysPerform')}
                    tooltipPlacement="bottom"
                    tooltipWidth={250}
                    emailRequestTypeOfInterestedPackage={interestedPackageEnum.SYSTEM_CONTROL}
                    emailRequestButtonClicked={'Lock Button'}
                    emailRequestWhichScreen={'SSC'}
                  />
                </div>
              </EPTierProtection>
            </div>
          </div>
          <div className="assets-grid">
            {devicesList.map(device => {
              const isCompressor = compressorTypes.includes(device.deviceType);

              if (isCompressor) {
                return <ControlCenterCompressorWidget key={device.deviceId} compressor={device} />;
              }

              return <UniversalDeviceWidget key={device.deviceId} device={device} />;
            })}
            <EPTierProtection componentName={ComponentTierName.AddNewController}>
              <CreateNewControllerTier compressorName={`${devicesList.length}`} />
            </EPTierProtection>
          </div>
        </div>
      )}
    </Container>
  );
});

export default ControlCenterAssets;
